import { Card, Collapse, Collection, Link, SectionHeader } from '@ksyos/design-system';
import { isEmpty } from 'lodash';
import { useEffect, useState } from 'react';
import type { EpisodeCategory } from '@common/enums';
import type { IServiceGroupMembership, IServicesByServiceGroupForEpisodeCategory } from '@common/interfaces/';
import { ManagerAuthorization } from '@common/enums';
import { groupServiceGroupMembershipsByEpisodeCategory } from '@common/utils/groupServiceGroupMembershipsByEpisodeCategory';
import { getSelectedLanguage, translateKey } from '@frontend/translate';
import { hasOneAuthorization, useAbortableFetch } from '@frontend/utils';
import type { IServiceGroupSetting } from '../../interfaces/serviceGroupSetting';
import type { IServiceGroupSettingsByCategory } from '../../interfaces/serviceGroupSettingsByCategory';
import { EmptyListPlaceholder } from '../../elements/EmptyListPlaceholder';
import { GroupActiveCarePathDetailsCollectionItem } from './groupActiveCarePathDetails/GroupActiveCarePathDetailsCollectionItem';
import { GroupCarePathDetailsCollectionLinkItem } from './groupActiveCarePathDetails/GroupCarePathDetailsCollectionLinkItem';

export interface IGroupDetailsProps {
    groupCarePaths: IServiceGroupSettingsByCategory[];
    groupId: string;
}

interface IEpisodeCategoryServiceGroupSettings {
    episodeCategory: EpisodeCategory;
    settings: IServiceGroupSetting[];
}

export function GroupEpisodeCategoriesView({ groupId, groupCarePaths }: IGroupDetailsProps) {
    const abortableFetch = useAbortableFetch();
    const translate = getSelectedLanguage();
    const [serviceGroupMembershipsGroupedByEpisodeCategories, setServiceGroupMembershipsGroupedByEpisodeCategories] =
        useState<IServicesByServiceGroupForEpisodeCategory[]>([]);
    const [activeEpisodeCategoryServiceGroups, setActiveEpisodeCategoryServiceGroups] = useState<
        IServicesByServiceGroupForEpisodeCategory[]
    >([]);
    const [inactiveEpisodeCategoryServiceGroups, setInactiveEpisodeCategoryServiceGroups] = useState<
        IServicesByServiceGroupForEpisodeCategory[]
    >([]);

    const [showInactiveGroupCarePaths, setShowInactiveGroupCarePaths] = useState(false);

    useEffect(() => {
        loadActiveSubgroupsWithEpisodeCategory();
    }, [groupId]);

    useEffect(() => {
        processCarePaths();
    }, [groupCarePaths]);

    return (
        <>
            <SectionHeader title={translate.common.carePaths} />

            {carePathsCards()}
        </>
    );

    async function loadActiveSubgroupsWithEpisodeCategory() {
        const url = `/api/serviceGroupMemberships?groupId=${groupId}`;
        const result = await abortableFetch.fetchJson(url, 'GET');
        const serviceGroupMemberships = result.json.items as IServiceGroupMembership[];
        const groupedByEpisodeCategory = groupServiceGroupMembershipsByEpisodeCategory(serviceGroupMemberships);
        setServiceGroupMembershipsGroupedByEpisodeCategories(groupedByEpisodeCategory);
    }

    function carePathsCards() {
        if (isEmpty(groupCarePaths)) {
            return <EmptyListPlaceholder subtitle={translate.groups.noActiveCarePaths} />;
        }

        if (isEmpty(inactiveEpisodeCategoryServiceGroups)) {
            return (
                <Card>
                    <Collection type="ul">
                        {activeEpisodeCategoryServiceGroups.map((item) => renderItems(item, true))}
                    </Collection>
                </Card>
            );
        }

        if (isEmpty(activeEpisodeCategoryServiceGroups)) {
            return (
                <Card>
                    <Collection type="ul">
                        {inactiveEpisodeCategoryServiceGroups.map((item) => renderItems(item, false))}
                    </Collection>
                </Card>
            );
        }

        return (
            <Card>
                <Collection
                    aria-label="groepen lijst"
                    type="ul"
                >
                    {activeEpisodeCategoryServiceGroups.map((item) => renderItems(item, true))}
                    <Collapse isOpen={showInactiveGroupCarePaths}>
                        {inactiveEpisodeCategoryServiceGroups.map((item) => renderItems(item, false))}
                    </Collapse>
                </Collection>
                <Card.Footer>
                    <Link
                        color="minimal"
                        fontWeight="normal"
                        iconLeft={showInactiveGroupCarePaths ? 'caret-up' : 'caret-down'}
                        onClick={toggleShowInactiveCarePaths}
                        size="sm"
                        variant="button"
                    >
                        {showInactiveGroupCarePaths
                            ? translateKey('episodeCategory.foldInactiveEpisodeCategory')
                            : translateKey('episodeCategory.showInactiveEpisodeCategory')}
                    </Link>
                </Card.Footer>
            </Card>
        );
    }

    function processCarePaths() {
        const activeCarePaths = groupCarePaths
            .filter((carePath) => carePath.activeSettings.length > 0)
            .map((carePath) => {
                return {
                    episodeCategory: carePath.episodeCategory,
                    settings: carePath.activeSettings,
                } as IEpisodeCategoryServiceGroupSettings;
            });
        setActiveEpisodeCategoryServiceGroups([...getServiceGroupsByEpisodeCategory(activeCarePaths)]);

        const inactiveCarePaths = groupCarePaths
            .filter((settings) => settings.activeSettings.length === 0 && settings.inactiveSettings.length > 0)
            .map((carePath) => {
                return {
                    episodeCategory: carePath.episodeCategory,
                    settings: carePath.inactiveSettings,
                } as IEpisodeCategoryServiceGroupSettings;
            });

        setInactiveEpisodeCategoryServiceGroups([...getServiceGroupsByEpisodeCategory(inactiveCarePaths)]);
    }

    function getServiceGroupsByEpisodeCategory(carePaths: IEpisodeCategoryServiceGroupSettings[]) {
        const serviceGroupsByEpisodeCategory: IServicesByServiceGroupForEpisodeCategory[] = [];

        for (const serviceGroupMembershipsEpisodeCategory of serviceGroupMembershipsGroupedByEpisodeCategories) {
            const carePath = carePaths.find(
                (item) => item.episodeCategory === serviceGroupMembershipsEpisodeCategory.episodeCategory.name
            );

            if (carePath) {
                const serviceGroupByEpisodeCategory: IServicesByServiceGroupForEpisodeCategory = {
                    episodeCategory: serviceGroupMembershipsEpisodeCategory.episodeCategory,
                    serviceGroups: [],
                };
                serviceGroupsByEpisodeCategory.push(serviceGroupByEpisodeCategory);

                for (const serviceGroup of serviceGroupMembershipsEpisodeCategory.serviceGroups) {
                    let isServiceGroupActive = false;

                    for (const service of serviceGroup.services!) {
                        const activeServicesWithinServiceGroup = carePath.settings.find(
                            (item) => item.service.code === service.code
                        );

                        if (activeServicesWithinServiceGroup) {
                            isServiceGroupActive = true;
                            break;
                        }
                    }
                    if (isServiceGroupActive) {
                        serviceGroupByEpisodeCategory.serviceGroups.push(serviceGroup);
                    }
                }
            }
        }

        return serviceGroupsByEpisodeCategory;
    }

    function renderSubtext(episodeCategory: EpisodeCategory, isActive: boolean) {
        const episodeCategoryServiceGroups = isActive
            ? activeEpisodeCategoryServiceGroups
            : inactiveEpisodeCategoryServiceGroups;
        const serviceGroupMembershipsGroupedByEpisodeCategory = episodeCategoryServiceGroups.find(
            (category) => category.episodeCategory.name === episodeCategory
        );

        if (!serviceGroupMembershipsGroupedByEpisodeCategory) {
            return '';
        }

        let names = '';
        for (const serviceGroup of serviceGroupMembershipsGroupedByEpisodeCategory.serviceGroups) {
            names.length > 0 ? (names += `, ${serviceGroup.name}`) : (names += `${serviceGroup.name}`);
        }

        return names;
    }

    function renderItems(carePaths: IServicesByServiceGroupForEpisodeCategory, isActive: boolean) {
        const subtitle = renderSubtext(carePaths.episodeCategory.name, isActive);
        let title: string = translateKey(`episodeCategoryEnum.${carePaths.episodeCategory.name}`);

        if (!isActive) {
            title += ` (${translateKey('common.isInactive')})`;
        }

        if (hasOneAuthorization(ManagerAuthorization.groupAddEdit)) {
            return (
                <GroupCarePathDetailsCollectionLinkItem
                    episodeCategory={carePaths.episodeCategory.name}
                    isActive={isActive}
                    key={carePaths.episodeCategory.id}
                    subtitle={subtitle}
                    title={title}
                />
            );
        }

        return (
            <GroupActiveCarePathDetailsCollectionItem
                episodeCategory={carePaths.episodeCategory.name}
                key={carePaths.episodeCategory.id}
                subtitle={subtitle}
                title={title}
            />
        );
    }

    function toggleShowInactiveCarePaths() {
        setShowInactiveGroupCarePaths(!showInactiveGroupCarePaths);
    }
}
